import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", budget: "", message: "" };

const FinalCTA = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Please fill in name, email and company.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Booked! Our team will reach out within 24 hours.");
      setForm(initial);
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-blue-600/15 via-zinc-950 to-zinc-950 p-8 lg:p-14">
          <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-zinc-50"
                data-testid="final-cta-heading"
              >
                Ready to build campaigns that convert?
              </h2>
              <p className="mt-4 text-zinc-300 max-w-md">
                Book a free 30-minute strategy call with our team. Tell us about your brand and
                we'll outline a creator strategy on the spot.
              </p>
              <div className="mt-8 space-y-3 text-sm text-zinc-400">
                <div>· No-fluff strategy walkthrough</div>
                <div>· 3 vetted creator shortlist on the call</div>
                <div>· Sample ROI model for your category</div>
              </div>
            </div>

            <form
              onSubmit={submit}
              className="rounded-2xl border border-white/10 bg-zinc-950/70 backdrop-blur-xl p-6 lg:p-7 space-y-4"
              data-testid="discovery-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-zinc-300">Name</Label>
                  <Input
                    id="name"
                    placeholder="Riya Bansal"
                    value={form.name}
                    onChange={onChange("name")}
                    className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                    data-testid="lead-input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="company" className="text-zinc-300">Company</Label>
                  <Input
                    id="company"
                    placeholder="Nykaa"
                    value={form.company}
                    onChange={onChange("company")}
                    className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                    data-testid="lead-input-company"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email" className="text-zinc-300">Work email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@brand.com"
                  value={form.email}
                  onChange={onChange("email")}
                  className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                  data-testid="lead-input-email"
                />
              </div>
              <div>
                <Label htmlFor="budget" className="text-zinc-300">Budget range</Label>
                <Input
                  id="budget"
                  placeholder="₹5L – ₹15L"
                  value={form.budget}
                  onChange={onChange("budget")}
                  className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                  data-testid="lead-input-budget"
                />
              </div>
              <div>
                <Label htmlFor="message" className="text-zinc-300">Tell us about your campaign</Label>
                <Textarea
                  id="message"
                  rows={3}
                  placeholder="What are you launching, and when?"
                  value={form.message}
                  onChange={onChange("message")}
                  className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                  data-testid="lead-input-message"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white h-11 glow-blue"
                data-testid="lead-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Booking...
                  </>
                ) : (
                  "Schedule a Call"
                )}
              </Button>
              <p className="text-[11px] text-zinc-500 text-center">
                By submitting, you agree to be contacted by the Bagfluence team.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
